import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketInsightPriceIndexState from '@app/model/market_insight/MarketInsightPriceIndexState';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import Utilities from '@util/Utilities';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import { useQuery } from '@tanstack/react-query';
import Portlet from '@app/component/portlet/Portlet';

// marketAreaId: the market of the market tab; null = the market of the hotel.
const MarketInsightPriceIndexFragment: React.FunctionComponent<{ statDate: string, marketAreaId: number | null }> = ({ statDate, marketAreaId }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: priceIndex, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findPriceIndex({ companyHotelId: companyHotel.id, statDate: statDate, marketAreaId: marketAreaId }),
		queryKey: MarketInsightQueryKeys.priceIndex(companyHotel.id, statDate, marketAreaId),
	});

	if (isLoading) {
		return <Portlet title="Price Index">
			<div className="animate-pulse flex items-center gap-x-2">
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5"/>
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5"/>
			</div>
		</Portlet>;
	}
	if (isSuccess)
		return <Portlet title="Price Index">
			{(priceIndex === undefined || priceIndex === null || priceIndex.state === MarketInsightPriceIndexState.NOT_AVAILABLE) &&
				<p className="block text-xs font-light text-gray-400">Not Available</p>}
			{priceIndex !== undefined && priceIndex !== null && priceIndex.state === MarketInsightPriceIndexState.AVAILABLE && <>
				<div className="flex flex-wrap items-center gap-x-2">
					<div className="grow">
						<div className="flex items-center gap-x-2">
							{displayChange(priceIndex.marketPriceMedianDifference)}
						</div>
						<p className="block text-xs font-light text-gray-400">Market vs {priceIndex.periodInDays} days ago</p>
					</div>
					<div className="mx-3 h-6 w-px bg-gray-100 block"></div>
					<div className="grow">
						<div className="flex items-center gap-x-2">
							{displayChange(priceIndex.myPropertyPriceMedianDifference)}
						</div>
						<p className="block text-xs font-light text-gray-400">My property</p>
					</div>
				</div>
			</>}
			{priceIndex !== undefined && priceIndex !== null && priceIndex.state === MarketInsightPriceIndexState.SOLD_OUT && <>
				<div className="flex flex-wrap items-center gap-x-2">
					<div className="grow">
						<div className="flex items-center gap-x-2">
							{displayChange(priceIndex.marketPriceMedianDifference)}
						</div>
						<p className="block text-xs font-light text-gray-400">Market</p>
					</div>
					<div className="mx-3 h-6 w-px bg-gray-100 block"></div>
					<div className="grow">
						<div className="flex items-center gap-x-2">
							<p className="text-2xl font-semibold text-gray-900">Sold Out</p>
						</div>
						<p className="block text-xs font-light text-gray-400">My property</p>
					</div>
				</div>
			</>}
		</Portlet>;

	return <Portlet title="Price Index">
		<p className="block text-xs font-light text-gray-400">Not Available</p>
	</Portlet>;
};

// An absent change shows "n/a"; Utilities.formatDouble prints "0" for null.
const displayChange = (change: number | null) => {
	if (change == null) return <p className="text-2xl font-semibold text-gray-400">n/a</p>;
	return <p className="text-2xl font-semibold text-gray-900">{Utilities.formatDouble(change * 100, 0)}%</p>;
};

export default MarketInsightPriceIndexFragment;